import type { StoredGroup } from '~/gql/groups/groups'
import type { EditableGroup } from '~/pages/backoffice/admin/groups/components/GroupModal'
import { useQuery } from '@apollo/client/react'
import { Loader, MoreHorizontal, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_GROUPS } from '~/gql/groups/groups'
import { GQL_USERS_WITH_FILTERS } from '~/gql/users/users'
import GroupDeleteModal from '~/pages/backoffice/admin/groups/components/GroupDeleteModal'
import GroupModal from '~/pages/backoffice/admin/groups/components/GroupModal'

/**
 * Gestion des groupes d'une organisation (#135) : créer, renommer, supprimer, et
 * composer les groupes. Vit à côté de la page Utilisateurs, dont elle est la
 * maille supérieure.
 */
function GroupsTable() {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_GROUPS, { fetchPolicy: 'cache-and-network' })
  const { data: usersData } = useQuery(GQL_USERS_WITH_FILTERS, {
    variables: { input: { skip: 0, take: 1000, searchInput: '' } },
  })

  const [openModal, setOpenModal] = useState(false)
  const [editing, setEditing] = useState<EditableGroup | undefined>()
  const [deleting, setDeleting] = useState<StoredGroup | undefined>()

  const groups = data?.groups ?? []
  const users = usersData?.getFilteredUsers?.users ?? []

  const openCreate = () => {
    setEditing(undefined)
    setOpenModal(true)
  }

  const openEdit = (group: StoredGroup) => {
    setEditing({ id: group.id, name: group.name, description: group.description })
    setOpenModal(true)
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>{t('groups.list.title')}</CardTitle>
          <Button onClick={openCreate}>
            <Plus className="mr-2 size-4" />
            {t('groups.new')}
          </Button>
        </CardHeader>
        <CardContent>
          {loading && groups.length === 0
            ? <div className="flex justify-center p-6"><Loader className="size-6 animate-spin" /></div>
            : groups.length === 0
              ? <p className="p-4 text-center text-sm text-muted-foreground">{t('groups.empty')}</p>
              : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('common.name')}</TableHead>
                        <TableHead>{t('common.description')}</TableHead>
                        <TableHead>{t('groups.memberCount')}</TableHead>
                        <TableHead className="w-[80px] text-right">{t('common.action')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groups.map(group => (
                        <TableRow key={group.id}>
                          <TableCell className="font-medium">{group.name}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {group.description || '—'}
                          </TableCell>
                          <TableCell>{group.memberCount}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="size-8 p-0">
                                  <span className="sr-only">{t('common.action')}</span>
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEdit(group)}>
                                  {t('common.update')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setDeleting(group)}
                                >
                                  {t('common.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
        </CardContent>
      </Card>

      {openModal && (
        <GroupModal
          open={openModal}
          onCancel={() => { setOpenModal(false); setEditing(undefined) }}
          onSuccess={refetch}
          users={users}
          group={editing}
        />
      )}

      {deleting && (
        <GroupDeleteModal
          open={!!deleting}
          onCancel={() => setDeleting(undefined)}
          onSuccess={refetch}
          group={deleting}
        />
      )}
    </div>
  )
}

export default GroupsTable
